import { Link } from 'react-router';
import type { FillSharedLineDto } from '@pams/shared';
import { POST_COPY, SHARED_LINES_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §2h] THE COLUMNS THIS DUTY IS COVERED FOR — READ-ONLY, FROM ANOTHER DUTY POINT'S SHEET.
 *
 *  A covered duty carries no line for a shared column, so `FillLines` cannot show it and the
 *  person standing it would be invisible on this screen. This says who that is and where they are
 *  named — and nothing on it is a control: naming happens on the anchor duty's own sheet, so the
 *  six verbs of `fillPermissions.ts` have nothing to govern here.
 *
 *  ⚠️ ITS OWN TEST IDS — `fill-shared-line` and `fill-shared-officer`, NEVER `fill-line`.
 *  `fillGaps.spec.ts` reaches every chip grid THROUGH `fill-line` and filters null rows out, so a
 *  block wearing that id with no grid would be dropped from the measurements rather than failing.
 *  ⚠️ AND NO `title` ANYWHERE, for that spec's other rule: it finds a row's scope chip as its
 *  FIRST `[title]`.
 *
 *  ⚠️ THE NAMES ARE THE SERVER'S NARROWED LIST — only the shares this caller can see on the anchor
 *  duty, and `?division=` narrows them too. An empty list with no share is "named by the division
 *  asked for it there", never "nobody": a division that cannot see the anchor's share must not be
 *  told the duty is unstaffed.
 *
 *  ⚠️ [P7A §3] A SHARED VEHICLE COLUMN NAMES NOBODY, ANYWHERE. It states where it is required and that
 *  there is no vehicle mapping — never "Nobody named yet" and never a link to name them, which would be
 *  a vehicle short-fall and an act nobody can perform.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const SharedLines = ({
  lines,
  division,
}: {
  lines: readonly FillSharedLineDto[];
  /**
   * ⚠️ THE `?division=` THIS SHEET WAS NARROWED TO, CARRIED ONTO THE ANCHOR LINK — [C3c §2]'s rule
   * that it rides on every child link. The covered names above were narrowed by it; a link that
   * dropped it would open the anchor's sheet on every division's lines, and a super admin's
   * `Complete with short-fall` there would close a division they were not looking at.
   */
  division?: string | undefined;
}) => (
  <div className="flex flex-col gap-3" data-testid="fill-shared-lines">
    <h3 className="pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {SHARED_LINES_COPY.heading}
    </h3>
    {lines.map((line) => {
      const dutyPoint = `DP${String(line.anchor.ordinal)}`;
      return (
        <section key={line.lineKey} data-testid="fill-shared-line" data-line-key={line.lineKey}>
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-medium">{line.label}</span>
            <span className="text-xs text-muted-foreground">
              {SHARED_LINES_COPY.sharedFrom(dutyPoint, line.anchor.dutyPointTitle)}
            </span>
          </div>
          {line.duties.map((duty) =>
            line.kind === 'vehicle' ? (
              <div key={duty.deploymentId} className="mt-1 flex flex-col gap-1 pl-4" data-testid="fill-shared-vehicle">
                <span className="text-xs text-muted-foreground">{duty.slotLabel}</span>
                <span className="text-sm text-muted-foreground">
                  {SHARED_LINES_COPY.vehicleOn(dutyPoint)} · {POST_COPY.vehicleNoMapping}
                </span>
              </div>
            ) : (
              <div key={duty.deploymentId} className="mt-1 flex flex-col gap-1 pl-4">
                <span className="text-xs text-muted-foreground">{duty.slotLabel}</span>
                {duty.officers.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {duty.officers.map((officer) => (
                      <li
                        key={officer.id}
                        data-testid="fill-shared-officer"
                        className="rounded-md border border-border px-2 py-1 text-sm"
                      >
                        {officer.rankShortName ? `${officer.name} · ${officer.rankShortName}` : officer.name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {duty.callerHasShare ? SHARED_LINES_COPY.nobodyYet : SHARED_LINES_COPY.notVisible}
                  </span>
                )}
                {duty.callerHasShare ? (
                  <Link
                    to={`/duties/${duty.deploymentId}${division ? `?division=${division}` : ''}`}
                    className="w-fit text-sm text-link underline-offset-4 hover:underline"
                    data-testid="fill-shared-name-them"
                  >
                    {SHARED_LINES_COPY.nameThem(dutyPoint)}
                  </Link>
                ) : (
                  <span className="text-sm text-muted-foreground" data-testid="fill-shared-named-on">
                    {SHARED_LINES_COPY.namedOn(dutyPoint)}
                  </span>
                )}
              </div>
            ),
          )}
        </section>
      );
    })}
  </div>
);
